/**
 * Liste des plans de site : titre affiché, publication publique.
 * Upload / géoréf restent dans le gestionnaire desktop pour l’instant.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  fetchOrganizationSitePlans,
  updateOrganizationSitePlanMeta,
  type OrganizationSitePlanRow,
  HttpError,
} from '../services/organizationApi'
import './OrganizationSitePlansEditor.css'
import { useTranslation } from 'react-i18next'

type Props = {
  organizationId: number
}

export function OrganizationSitePlansEditor({ organizationId }: Props) {
  const { t } = useTranslation('organizations')
  const [plans, setPlans] = useState<OrganizationSitePlanRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [draftNames, setDraftNames] = useState<Record<number, string>>({})

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchOrganizationSitePlans(organizationId)
      const rows = (data.site_plans || []).filter(
        (p): p is OrganizationSitePlanRow & { id: number } => typeof p.id === 'number',
      )
      setPlans(rows)
      const names: Record<number, string> = {}
      for (const p of rows) names[p.id] = p.name || ''
      setDraftNames(names)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('OrganizationSitePlansEditor.erreur_chargement'))
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void reload()
  }, [reload])

  const saveMeta = async (
    planId: number,
    patch: { name?: string; is_published?: boolean; is_visible?: boolean },
  ) => {
    setSavingId(planId)
    setError(null)
    try {
      const data = await updateOrganizationSitePlanMeta(organizationId, planId, patch)
      const rows = (data.site_plans || []).filter(
        (p): p is OrganizationSitePlanRow & { id: number } => typeof p.id === 'number',
      )
      setPlans(rows)
      const names: Record<number, string> = {}
      for (const p of rows) names[p.id] = p.name || ''
      setDraftNames(names)
    } catch (err) {
      const msg =
        err instanceof HttpError
          ? typeof err.data === 'object' && err.data
            ? JSON.stringify(err.data)
            : err.message
          : err instanceof Error
            ? err.message
            : 'Erreur'
      setError(msg)
    } finally {
      setSavingId(null)
    }
  }

  if (loading) {
    return (
      <div className="org-site-plans">
        <h2 className="org-site-plans__title">{t('OrganizationSitePlansEditor.cartes_plans_site')}</h2>
        <p className="org-site-plans__hint">{t('OrganizationSitePlansEditor.chargement')}</p>
      </div>
    )
  }

  return (
    <div className="org-site-plans">
      <h2 className="org-site-plans__title">{t('OrganizationSitePlansEditor.cartes_plans_site')}</h2>
      <p className="org-site-plans__hint">
        {t('OrganizationSitePlansEditor.plans_uploades_dans_gestionnaire_apparai')}
      </p>
      {error && <p className="org-site-plans__error">{error}</p>}
      {plans.length === 0 ? (
        <p className="org-site-plans__empty">
          {t('OrganizationSitePlansEditor.aucun_plan_site_moment_ajoutez')}
        </p>
      ) : (
        <ul className="org-site-plans__list">
          {plans.map((plan) => {
            const id = plan.id as number
            const title = draftNames[id] ?? plan.name ?? ''
            const canPublish = title.trim().length > 0 && plan.is_visible
            const busy = savingId === id
            return (
              <li key={id} className="org-site-plans__row">
                <div className="org-site-plans__fields">
                  <label className="org-site-plans__label" htmlFor={`site-plan-name-${id}`}>
                    {t('OrganizationSitePlansEditor.titre_affiche')}
                  </label>
                  <input
                    id={`site-plan-name-${id}`}
                    className="org-site-plans__input"
                    value={title}
                    disabled={busy}
                    onChange={(e) =>
                      setDraftNames((prev) => ({ ...prev, [id]: e.target.value }))
                    }
                    onBlur={() => {
                      if (title.trim() !== (plan.name || '').trim()) {
                        void saveMeta(id, { name: title.trim() })
                      }
                    }}
                    placeholder={t('OrganizationSitePlansEditor.ex_carte_ete_2025')}
                  />
                  {plan.url && (
                    <a
                      className="org-site-plans__file"
                      href={plan.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('OrganizationSitePlansEditor.voir_fichier')}
                    </a>
                  )}
                </div>
                <div className="org-site-plans__actions">
                  <label className="org-site-plans__check">
                    <input
                      type="checkbox"
                      checked={plan.is_visible}
                      disabled={busy}
                      onChange={(e) =>
                        void saveMeta(id, { is_visible: e.target.checked })
                      }
                    />
                    {t('OrganizationSitePlansEditor.visible_editeur')}
                  </label>
                  <label className="org-site-plans__check">
                    <input
                      type="checkbox"
                      checked={plan.is_published}
                      disabled={busy || (!plan.is_published && !canPublish)}
                      title={
                        !canPublish && !plan.is_published
                          ? t('OrganizationSitePlansEditor.titre_requis_plan_visible_publier')
                          : undefined
                      }
                      onChange={(e) =>
                        void saveMeta(id, {
                          name: title.trim(),
                          is_published: e.target.checked,
                        })
                      }
                    />
                    {t('OrganizationSitePlansEditor.publie_fiche_publique')}
                  </label>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
